import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Optional, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { AssetType } from '../../../../models/defines';
import { FolderNameDialog } from '../../../../components/dialogs/folder-name/folder-name-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfirmComponent } from '../../../../components/dialogs/confirm/confirm.component';
import { Subject, takeUntil } from 'rxjs';
import { AnalyticsNotifierService } from '../../../../services/utils/analytics-notifier.service';
import { IUploadedFileMetadata } from '../../../../models/files/file.model';
import { WorkspaceAssetStateManagerService } from '../../../../services/workspace/state-management/associations/workspace-asset-state-manager.service';
import { WorkspaceAssetActionService } from '../../../../services/workspace/actions/workspace-asset-action.service';
import { AssetsLibraryServiceService } from './asset-library/assets-library-service.service';
import { ComponentId } from './assets-main/assets-main.component';
import { ILocalAsset } from '../../../../models/assets/assets.model';

function pageRangePaginatorIntl(): MatPaginatorIntl {
    const intl = new MatPaginatorIntl();
    intl.getRangeLabel = (page: number, pageSize: number, length: number) => {
        if (length === 0) return '0 of 0';
        const totalPages = Math.ceil(length / pageSize);
        return `${page + 1} of ${totalPages}`;
    };
    return intl;
}

export interface IAssetData {
    assetRelativePath: string;
    assetId?: string;
    asset: ILocalAsset;
}
export interface IComponentUpdateWithAsset {
    component: ComponentId;
    asset: ILocalAsset;
}
@Component({
    selector: 'app-assets',
    templateUrl: './assets.component.html',
    styleUrls: ['./assets.component.scss'],
    providers: [{ provide: MatPaginatorIntl, useFactory: pageRangePaginatorIntl }],
})
export class AssetsComponent implements OnInit, OnDestroy {
    @Input({ alias: 'displayGenerate' }) displayGenerate = true;
    @Input({ alias: 'hosted' }) hosted = false;
    @Input({ alias: 'toDisplayLoading' }) toDisplayLoading = false;
    @Input({ alias: 'toDisplayFileDrop' }) toDisplayFileDrop = true;
    @Input({ alias: 'enableEditImage' }) enableEditImage = false;
    @Input({ alias: 'enableImageToVideo' }) enableImageToVideo = false;

    @Input({ alias: 'toShowSelectAsset' }) toShowSelectAsset = true;
    selectedAsset: ILocalAsset = null;

    @Output() selectedUrl = new EventEmitter<ILocalAsset>();
    @Output() updateComponent = new EventEmitter<IComponentUpdateWithAsset>();
    @Output() extendVideoEventEmitter = new EventEmitter<ILocalAsset>();
    cols: number;
    loadingData = false;
    gridByBreakpoint = {
        xl: 6,
        lg: 4,
        md: 3,
        sm: 2,
        xs: 1,
    };
    @Input({ alias: 'filterAssetBy' })
    filterAssetBy: AssetType;
    dialogType: AssetType;
    AssetType = AssetType;
    currentAsset: ILocalAsset;
    onDestroy$ = new Subject<boolean>();
    currentFolder: string;
    isRootFolder = false;
    toDisplayExtendVideoOption = false;

    folders$ = this.wsAssetState.folders$;
    assets$ = this.wsAssetState.assets$;
    foldersPagination$ = this.wsAssetState.foldersPagination$;
    assetsPagination$ = this.wsAssetState.assetsPagination$;
    isFoldersLoading$ = this.wsAssetState.isFoldersLoading$;
    isAssetsLoading$ = this.wsAssetState.isAssetsLoading$;

    constructor(
        private breakpointObserver: BreakpointObserver,
        private dialog: MatDialog,
        private _snackBar: MatSnackBar,
        private analyticsNotifierService: AnalyticsNotifierService,
        private wsAssetState: WorkspaceAssetStateManagerService,
        public wsAssetActionService: WorkspaceAssetActionService,
        private assetsLibraryService: AssetsLibraryServiceService,
        @Optional() @Inject(MAT_DIALOG_DATA) public data
    ) {}

    // output to navigate to generate image (add the asset)
    @Input({ alias: 'selectAsset', required: false })
    set setImmediateAsset(asset: ILocalAsset) {
        if (!asset) {
            return;
        }
        this.selectFile(asset);
    }

    ngOnInit(): void {
        this.hosted = this.hosted || this.data?.hosted;

        this.filterAssetBy = this.data?.dialogType || this.filterAssetBy;

        this.wsAssetState.setFilterType(this.dialogType);
        this.subscribeToWorkspaceState();
        this.setExpendVideoAsync();

        this.breakpointObserver
            .observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge])
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((result) => {
                if (result.matches) {
                    if (result.breakpoints[Breakpoints.XSmall]) {
                        this.cols = this.gridByBreakpoint.xs;
                    }
                    if (result.breakpoints[Breakpoints.Small]) {
                        this.cols = this.gridByBreakpoint.sm;
                    }
                    if (result.breakpoints[Breakpoints.Medium]) {
                        this.cols = this.gridByBreakpoint.md;
                    }
                    if (result.breakpoints[Breakpoints.Large]) {
                        this.cols = this.gridByBreakpoint.lg;
                    }
                    if (result.breakpoints[Breakpoints.XLarge]) {
                        this.cols = this.gridByBreakpoint.xl;
                    }
                }
            });
    }
    private async setExpendVideoAsync() {
        await this.assetsLibraryService.setFeatureAccessForGenerateAsync();
        this.toDisplayExtendVideoOption = this.assetsLibraryService.toDisplayExtendVideoOption;
    }

    extendVideo(asset: ILocalAsset) {
        this.extendVideoEventEmitter.emit(asset);
    }

    uploadFileFinished(uploadedFileMetadata: IUploadedFileMetadata) {
        if (uploadedFileMetadata) {
            this.analyticsNotifierService.notifyEvent(`Asset Upload`, uploadedFileMetadata);
        }
        // this.wsAssetState.getAssetsForWSAsync();
    }

    selectFile(file: ILocalAsset) {
        this.currentAsset = file;
        console.log(file);
        if (!file) {
            return;
        }
        const assetData: IAssetData = {
            assetId: file._id,
            assetRelativePath: file.uploadPath,
            asset: file,
        };
        if (this.hosted) {
            this.selectedUrl.emit(file);
        }
        if (!this.hosted) {
            return;
        }
        if (this.selectedAsset === file) {
            // Deselect
            this.selectedAsset = null;
        } else {
            this.selectedAsset = file;
        }
    }

    moveFile(file: ILocalAsset, toFolder: string) {
        console.log('to folder', toFolder);

        this.dialog
            .open(ConfirmComponent, {
                minHeight: 'fit-content',
                data: {
                    message: `Are you sure you want to move ${file.displayName} to ${toFolder}?`,
                },
            })
            .afterClosed()
            .subscribe((response) => {
                if (response?.confirmed) {
                    console.log('Moving file');
                    this.wsAssetActionService.moveFileAsync(
                        { assetIds: file._id, toFolder: toFolder },
                        file.displayName,
                        file.folder
                    );
                }
            });
    }

    convertImageToVideo(file: ILocalAsset) {
        this.updateComponent.emit({ component: 'generate-video', asset: file });
    }

    editImage(file: ILocalAsset) {
        this.updateComponent.emit({ component: 'generate-image', asset: file });
    }

    renameFile(file: ILocalAsset) {
        const oldName = file?.displayName + '';
        this.dialog
            .open(FolderNameDialog, {
                minHeight: 'fit-content',
                data: { isNew: false, oldName: oldName, fileMode: true },
            })
            .afterClosed()
            .subscribe((response) => {
                if (response?.newName) {
                    console.log('Sending file name change');
                    this.wsAssetActionService.renameFileAsync(
                        {
                            newName: response.newName,
                            assetId: file._id,
                        },
                        oldName
                    );
                }
            });
    }

    deleteFile(file: ILocalAsset) {
        this.dialog
            .open(ConfirmComponent, {
                minHeight: 'fit-content',
                data: {
                    message: `Are you sure you want to delete ${file.displayName}?`,
                },
            })
            .afterClosed()
            .subscribe((response) => {
                if (response?.confirmed) {
                    console.log('Deleting folder');
                    this.wsAssetActionService.removeAssetAsync(file._id, file.displayName);
                }
            });
    }

    // renameFolder(folderName: string, folderId: string) {
    //     this.dialog
    //         .open(FolderNameDialog, {
    //             minHeight: 'fit-content',
    //             data: { isNew: false, oldName: folderName, fileMode: false },
    //         })
    //         .afterClosed()
    //         .subscribe((response) => {
    //             if (response?.newName) {
    //                 console.log('Sending new folder');
    //                 this.wsAssetActionService.renameFolderAsync(folderName, {
    //                     newName: response.newName,
    //                     assetId: folderId,
    //                 });
    //             }
    //         });
    // }

    selectFolder(folderName: string) {
        if (!folderName) {
            return;
        }
        this.assetsLibraryService.setCurrentFolder(folderName);
    }

    openNewFolderDialog() {
        this.dialog
            .open(FolderNameDialog, {
                minHeight: 'fit-content',
                data: { isNew: true, oldName: null, fileMode: false },
            })
            .afterClosed()
            .subscribe((response) => {
                //{newName: 'gaegaa', oldName: '', isNew: true}
                console.log(response);
                if (response?.newName) {
                    console.log('Sending new folder');
                    this.wsAssetActionService.createFolderAsync(response?.newName);
                }
            });
    }

    ngOnDestroy(): void {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    private subscribeToWorkspaceState() {
        this.wsAssetState.currentFolderName$.pipe(takeUntil(this.onDestroy$)).subscribe((folderName) => {
            this.currentFolder = folderName;
            this.isRootFolder = this.currentFolder === '/';
        });
        this.wsAssetState.isLoading$.pipe(takeUntil(this.onDestroy$)).subscribe((isLoading) => {
            this.loadingData = isLoading;
        });
        this.wsAssetActionService.successMessage$.pipe(takeUntil(this.onDestroy$)).subscribe((message) => {
            if (!message) {
                return;
            }
            this._snackBar.open(message, 'Dismiss', { duration: 3000 });
        });
        this.wsAssetActionService.actionError$.pipe(takeUntil(this.onDestroy$)).subscribe((message) => {
            if (!message) {
                return;
            }
            this._snackBar.open(message, 'Dismiss', { duration: 3000 });
        });
    }

    onFolderPageChange(page: number) {
        this.wsAssetState.setFolderPage(page);
    }
    onAssetPageChange(page: number) {
        this.wsAssetState.setAssetPage(page);
    }
    exitFolder() {
        this.assetsLibraryService.setCurrentFolder(null);
    }
}
